import { useMutation } from "@tanstack/react-query";
import usePrivateAxios from "@/common/api/usePrivateAxios";
import { unFormatCurrency, unFormatUnit, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { unitStrategy } from "@/common/constants/unitStrategies.contants";
import { EquipmentValidationSchema } from "../schemas/create.schema";

interface UseCreateCompanyEquipment {
  onSuccess: () => void,
  onError: (error: any) => void,
}

const useCreateEquipment = ({ onSuccess, onError }: UseCreateCompanyEquipment) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['createEquipment'],
    mutationFn: async (form: EquipmentValidationSchema) => {
      const payload = {
        photos_attachments_attributes: form.photos?.length ? form.photos.map((photo) => ({ blob_id: photo.id })) : undefined,
        branch_id: form.branch_id.value,
        card_attributes: {
          card_product_limits_attributes: form.card_product_limits_attributes
            ? form.card_product_limits_attributes?.map((cardProductLimit) => {
              return {
                product_id: cardProductLimit.product_id,
                quantity_limit_per_transaction: (cardProductLimit.limitByTransaction && cardProductLimit.transactionLimit?.quantity && unFormatUnknownUnit(cardProductLimit.transactionLimit?.quantity) > 0)
                  ? unFormatUnknownUnit(cardProductLimit.transactionLimit?.quantity)
                  : undefined,
                credit_limit_per_transaction: (cardProductLimit.limitByTransaction && cardProductLimit.transactionLimit?.totalValue)
                  ? unFormatCurrency(cardProductLimit.transactionLimit?.totalValue)
                  : undefined,
                period_in_days: cardProductLimit.limitByPeriod ? cardProductLimit.periodLimit?.period?.value : undefined,
                quantity_limit_per_period: (cardProductLimit.limitByPeriod && cardProductLimit.periodLimit?.quantity && unFormatUnknownUnit(cardProductLimit.periodLimit?.quantity) > 0)
                  ? unFormatUnknownUnit(cardProductLimit.periodLimit?.quantity)
                  : undefined,
                credit_limit_per_period: (cardProductLimit.limitByPeriod && cardProductLimit.periodLimit?.totalValue)
                  ? unFormatCurrency(cardProductLimit.periodLimit?.totalValue)
                  : undefined,
              };
            })
            : undefined,
          restriction_attributes: {
            period_restrictions_attributes: form.enablePeriodRestriction
              ? form.period_restrictions_attributes?.map((periodRestriction) => {
                return {
                  id: periodRestriction.id,
                  week_day: periodRestriction.week_day,
                  intervals_attributes: [
                    ...(() => {
                      const newIntervals = [] as { start_time?: string | null, end_time?: string | null; }[];

                      if (periodRestriction.blocked) {
                        return [];
                      }

                      if (periodRestriction.intervals_attributes?.some((interval) => interval.start_time?.value === 'all_day')) {
                        newIntervals.push({ start_time: '00:00', end_time: '23:59' });
                        return newIntervals;
                      }

                      periodRestriction?.intervals_attributes?.forEach((interval) => {
                        newIntervals.push({ start_time: interval.start_time?.value, end_time: interval.end_time?.value });
                      });
                      return newIntervals;
                    })(),
                  ]
                };
              })
              : undefined,
          },
        },
        category_id: form.equipment_category.value,
        model_id: form.model?.value,
        equipment_tier_id: form.equipment_tier?.value || null,
        tank_capacity: form.tank_capacity ? unFormatUnit(form.tank_capacity, unitStrategy['liters']) : undefined,
        last_usage_value: form.last_usage_value ? unFormatUnit(form.last_usage_value, form?.unit_for_consumption ? unitStrategy[form.unit_for_consumption as keyof typeof unitStrategy] : '') : undefined,
        official_name: form.official_name || undefined,
        serial_number: form.serial_number || undefined,
        license_plate: form.license_plate || undefined,
        chassis_number: form.chassis_number || undefined,
        renavam: form.renavam || undefined,
        usage_kind: form.usage_kind.value || undefined,
        property_kind: form.property_kind?.value || undefined,
        property_code: form.property_code || undefined,
        documents_attachments_attributes: form.documents?.length ? form.documents.map((document) => ({ blob_id: document.id })) : undefined,
      };

      return privateAxios.post('/equipments', { equipment: payload });
    },
    onSuccess,
    onError,
  });
};

export default useCreateEquipment;
